'use client';

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionService } from '@/lib/services/subscriptionService';
import { useRouter } from 'next/navigation';

// Stripe Price IDs — replace with your actual Stripe Price IDs
const PRICE_IDS: Record<string, string> = {
  Silver: process.env.NEXT_PUBLIC_STRIPE_PRICE_SILVER || '',
  Gold: process.env.NEXT_PUBLIC_STRIPE_PRICE_GOLD || '',
  Platinum: process.env.NEXT_PUBLIC_STRIPE_PRICE_PLATINUM || '',
  Diamond: process.env.NEXT_PUBLIC_STRIPE_PRICE_DIAMOND || '',
};

interface PricingTier {
  name: string;
  price: string;
  period: string;
  desc: string;
  features: string[];
  cta: string;
  highlight: boolean;
}

interface SubscribeButtonProps {
  tier: PricingTier;
}

export default function SubscribeButton({ tier }: SubscribeButtonProps) {
  const { user } = useAuth();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    setError(null);

    if (!user) {
      router.push('/dashboard');
      return;
    }

    const priceId = PRICE_IDS[tier.name];
    if (!priceId) {
      setError('Subscription not available. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      const url = await subscriptionService.createCheckoutSession(priceId, tier.name.toLowerCase());
      if (url) {
        window.location.href = url;
      }
    } catch (err: any) {
      setError(err?.message ?? 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleSubscribe}
        disabled={loading}
        className={tier.highlight ? 'btn-primary w-full justify-center' : 'btn-outline w-full justify-center'}
        style={{
          textAlign: 'center',
          opacity: loading ? 0.7 : 1,
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? (
          <span className="flex items-center gap-2 justify-center">
            <svg className="animate-spin" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeDashoffset="10" />
            </svg>
            Processing...
          </span>
        ) : (
          tier.cta
        )}
      </button>
      {error && (
        <p className="text-xs mt-2 text-center" style={{ color: '#f87171', fontFamily: 'var(--font-mono)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
